import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Modal } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Fulfillment } from 'api/models/Fulfillment';
import { ApiService } from 'api/services/ApiService';
import { AttioQueryKeys } from 'common/constants/analyst-dashboards';
import { useEventBus } from 'context/EventBusContext/EventBusContext';
import { EventType } from 'context/EventBusContext/EventBusContext.type';
import { useAttioValuationContext } from 'dashboard409a/context/AttioValuationContext';
import { useCompanyMeasurementDateByAttioValuationId } from 'dashboard409a/hooks/useCompanyMeasurementDateByAttioValuationId';
import { useUndoableStatusChange } from 'dashboard409a/hooks/useUndoableStatusChange';
import {
  EmptyOperationsMessage,
  ModelStatus,
  ValuationDealInfo,
  ValuedEntity,
  ValuedInformation
} from 'dashboard409a/pages/Operations/components';
import { modelStatusMap } from 'dashboard409a/pages/Operations/components/ModelStatus/ModelStatus.constants';
import { NotesDisplay } from 'dashboard409a/pages/Operations/components/NotesDisplay';
import { ModelStatus as ModelStatusEnum, ValuationStatus } from 'dashboard409a/pages/Workboard/common/types';
import {
  sendFinalInvoice,
  updateValuationDealStage,
  updateValuationStatus,
} from 'dashboard409a/services/attio-common';
import { AttioValuationFormattedResponse } from 'dashboard409a/services/attio.types';
import { useCreateFulfillment, useFulfillmentList } from 'dashboard409a/services/fulfillment';
import type { AttioValuationDetailResponse } from 'dashboard409a/services/shared-types';
import { getFulfillmentSideEffectsForStatusChange } from 'dashboard409a/utilities/fulfillmentOnStatusChange';
import { Logger } from 'utilities';
import { BentoGrid, CloseButtonContainer, StyledModalBox } from './OperationsModal.styles';
import { OperationsModalProps, StatusRollbackSnapshot } from './OperationsModal.types';
import { addBusinessDays, useValuationDetail } from './useOperationsModal';

const OperationsModal: FC<OperationsModalProps> = ({
  open,
  onClose,
  data,
  isFromModels = false,
}) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { publish } = useEventBus();
  const { setCurrentAttioCompanyName, setCurrentAttioValuation } = useAttioValuationContext();
  const [optimisticStatus, setOptimisticStatus] = useState<{ statusId: string; label: string } | null>(null);
  const pendingPersistSnapshotRef = useRef<StatusRollbackSnapshot | null>(null);
  const undoFailureSnapshotRef = useRef<StatusRollbackSnapshot | null>(null);

  // Use the custom hook to handle valuation detail transformation and API calls
  const {
    valuationDetail,
    valuationId,
    isLoading,
    isError,
    error,
    refetchValuationDetail,
    statusOptions,
    isLoadingStatusOptions,
  } = useValuationDetail({ data, open });

  // Fetch company measurement date by attio_valuation_id - this includes the full company data
  const {
    data: companyMeasurementDateByAttioId,
    error: companyMeasurementDateError,
    isLoading: isLoadingCompanyMeasurementDate,
  } = useCompanyMeasurementDateByAttioValuationId(valuationId);

  // Extract company from companyMeasurementDateByAttioId
  const currentCompany = companyMeasurementDateByAttioId?.company;

  // Use the App company ID for fulfillment operations (not the Attio valued entity ID)
  // The fulfillment API expects the app company ID from companyMeasurementDateByAttioId
  const appCompanyId = useMemo(() => {
    if (currentCompany?.id) {
      return currentCompany.id.toString();
    }
    return undefined;
  }, [currentCompany?.id]);

  // Fetch fulfillment data to check if model support exists
  const { data: fulfillmentData, refetch: refetchFulfillments } = useFulfillmentList(appCompanyId);

  // Find existing fulfillment for the current company measurement date
  const existingFulfillment = useMemo(() => {
    if (!fulfillmentData?.results || !companyMeasurementDateByAttioId?.id) {
      return null;
    }
    return fulfillmentData.results.find(
      fulfillment => fulfillment.company_measurement_date === companyMeasurementDateByAttioId.id
    ) || null;
  }, [fulfillmentData, companyMeasurementDateByAttioId?.id]);

  // Mutation for creating fulfillment (used for manual model support creation)
  const createFulfillmentMutation = useCreateFulfillment(appCompanyId);

  const dismissUndoRef = useRef<() => void>(() => {});

  const applyOptimisticStatusDisplay = useCallback(
    (statusId: string) => {
      if (!valuationId) {
        return;
      }
      const selectedStatus = statusOptions.find(option => option.id === statusId);
      const label = selectedStatus?.label || 'status';
      setOptimisticStatus({ statusId, label });
      queryClient.setQueryData(
        [AttioQueryKeys.VALUATION_ITEM, valuationId],
        (old: AttioValuationDetailResponse | undefined) =>
          old ? { ...old, statusId, status: label } : old
      );
      publish(EventType.WORKBOARD_VALUATION_UPDATED, {
        valuationId,
        updates: { status: label },
      });
    },
    [valuationId, statusOptions, queryClient, publish]
  );

  const captureStatusRollbackSnapshot = useCallback((): StatusRollbackSnapshot => {
    const queryData = valuationId
      ? queryClient.getQueryData([AttioQueryKeys.VALUATION_ITEM, valuationId])
      : undefined;
    return {
      queryData,
      optimisticStatus,
      workboardStatusLabel: optimisticStatus?.label ?? valuationDetail?.status,
    };
  }, [valuationId, queryClient, optimisticStatus, valuationDetail?.status]);

  const restoreStatusRollbackSnapshot = useCallback(
    (snapshot: StatusRollbackSnapshot) => {
      if (valuationId) {
        const queryKey = [AttioQueryKeys.VALUATION_ITEM, valuationId];
        if (snapshot.queryData !== undefined) {
          queryClient.setQueryData(queryKey, snapshot.queryData);
        }
      }
      setOptimisticStatus(snapshot.optimisticStatus);
      if (valuationId && snapshot.workboardStatusLabel !== undefined) {
        publish(EventType.WORKBOARD_VALUATION_UPDATED, {
          valuationId,
          updates: { status: snapshot.workboardStatusLabel },
        });
      }
    },
    [valuationId, queryClient, publish]
  );

  const persistStatusChange = useCallback(
    async (statusId: string, snapshot: StatusRollbackSnapshot) => {
      if (!valuationId) {
        return;
      }
      try {
        await updateValuationStatus(valuationId, statusId);

        const selectedStatus = statusOptions.find(option => option.id === statusId);
        const statusLabel = selectedStatus?.label || 'status';

        let supportingDueDate = addBusinessDays(new Date(), 4);
        if (selectedStatus?.label === ValuationStatus.CLIENT_APPROVAL_RECEIVED) {
          supportingDueDate = addBusinessDays(new Date(), 3);
        }
        const sideEffects = getFulfillmentSideEffectsForStatusChange({
          statusLabel,
          existingFulfillment,
          companyMeasurementDateId: companyMeasurementDateByAttioId?.id,
          appCompanyId,
          supportingDueDate,
        });

        if (sideEffects.action === 'create' && sideEffects.createPayload) {
          await createFulfillmentMutation.mutateAsync(sideEffects.createPayload);
        } else if (sideEffects.action === 'update' && sideEffects.updatePayload && appCompanyId) {
          await ApiService.apiDashboardCompanyOperationsFulfillmentPartialUpdate(
            appCompanyId,
            valuationId,
            sideEffects.updatePayload as Fulfillment
          );
          refetchFulfillments();
        }

        publish(EventType.WORKBOARD_VALUATION_UPDATED, {
          valuationId,
          updates: { status: statusLabel },
        });

        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [AttioQueryKeys.VALUATION_ITEM, valuationId], exact: false }),
          queryClient.invalidateQueries({ queryKey: [AttioQueryKeys.VALUATION_STATUS_OPTIONS], exact: false }),
          queryClient.invalidateQueries({ queryKey: [AttioQueryKeys.CURRENT_VALUATIONS], exact: false }),
          queryClient.invalidateQueries({ queryKey: [AttioQueryKeys.PAST_VALUATIONS], exact: false }),
          queryClient.invalidateQueries({ queryKey: [AttioQueryKeys.VALUATIONS], exact: false }),
        ]);
        setOptimisticStatus(null);
      } catch (statusError) {
        Logger.error('Error updating valuation status:', statusError);
        restoreStatusRollbackSnapshot(snapshot);
        dismissUndoRef.current();
        enqueueSnackbar('Failed to update status. Please try again.', { variant: 'error' });
      }
    },
    [
      valuationId,
      statusOptions,
      existingFulfillment,
      companyMeasurementDateByAttioId?.id,
      appCompanyId,
      createFulfillmentMutation,
      queryClient,
      publish,
      refetchFulfillments,
      enqueueSnackbar,
      restoreStatusRollbackSnapshot,
    ]
  );

  const onCommitStatus = useCallback(
    async (statusId: string) => {
      const snap = pendingPersistSnapshotRef.current;
      if (!snap || !valuationId) {
        return;
      }
      await persistStatusChange(statusId, snap);
    },
    [valuationId, persistStatusChange]
  );

  const onUndoStatus = useCallback(
    async (previousStatusId: string) => {
      const snap = undoFailureSnapshotRef.current;
      if (!snap || !valuationId) {
        return;
      }
      await persistStatusChange(previousStatusId, snap);
    },
    [valuationId, persistStatusChange]
  );

  const onBeforeUndo = useCallback(
    (previousStatusId: string) => {
      undoFailureSnapshotRef.current = captureStatusRollbackSnapshot();
      applyOptimisticStatusDisplay(previousStatusId);
    },
    [captureStatusRollbackSnapshot, applyOptimisticStatusDisplay]
  );

  const { triggerChange, handleUndo, dismissUndo, isUndoPending, secondsLeft } = useUndoableStatusChange({
    valuationId,
    onCommit: onCommitStatus,
    onUndo: onUndoStatus,
    onBeforeUndo,
  });

  dismissUndoRef.current = dismissUndo;

  useEffect(() => {
    if (valuationDetail) {
      setCurrentAttioValuation(valuationDetail as AttioValuationFormattedResponse);
      if (valuationDetail?.valuedEntity.companyName) {
        setCurrentAttioCompanyName(valuationDetail?.valuedEntity.companyName);
      }
    }
  }, [valuationDetail, setCurrentAttioCompanyName, setCurrentAttioValuation]);

  useEffect(() => {
    if (!open) {
      setOptimisticStatus(null);
    }
  }, [open]);

  if (isError) {
    Logger.error('Error fetching Attio valuations:', error);
  }

  const handleStatusChange = async (statusId: string) => {
    if (!valuationId) {
      return;
    }
    const effectiveCurrentStatusId = optimisticStatus?.statusId ?? valuationDetail?.statusId;
    if (effectiveCurrentStatusId === statusId) {
      return;
    }

    const previousStatusId = effectiveCurrentStatusId;
    const snapshot = captureStatusRollbackSnapshot();
    applyOptimisticStatusDisplay(statusId);
    enqueueSnackbar('Valuation status has been updated', { variant: 'success' });
    pendingPersistSnapshotRef.current = snapshot;

    if (!previousStatusId) {
      onCommitStatus(statusId).catch(() => {});
      return;
    }

    triggerChange(statusId, previousStatusId).catch(() => {});
  };

  const handleDealStageChange = async (dealStageId: string) => {
    if (!dealStageId || !valuationDetail?.deal?.dealId) {
      return;
    }

    try {
      const { dealId } = valuationDetail.deal;
      await updateValuationDealStage(dealId, dealStageId);

      // Publish event to update the table row immediately
      publish(EventType.WORKBOARD_VALUATION_UPDATED, {
        valuationId,
        updates: {
          dealStage: dealStageId,
        },
      });

      // Invalidate all relevant caches
      await Promise.all([
        // Invalidate the valuation detail query
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.VALUATION_ITEM, valuationId],
          exact: false,
        }),
        // Invalidate the valuations list (for table refresh)
        queryClient.invalidateQueries({
          queryKey: ['valuations-all'],
          exact: false,
        }),
        // Invalidate current/past valuations
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.CURRENT_VALUATIONS],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.PAST_VALUATIONS],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.VALUATIONS],
          exact: false,
        }),
      ]);

      // Manually refetch the valuation detail to update the modal
      await refetchValuationDetail();

      enqueueSnackbar('Deal stage has been updated', { variant: 'success' });
    } catch (dealStageError) {
      Logger.error('Error updating deal stage:', dealStageError);
      enqueueSnackbar('Failed to update deal stage. Please try again.', { variant: 'error' });
    }
  };

  const handleSendFinalInvoice = async () => {
    await sendFinalInvoice(valuationDetail?.deal?.dealId as string);

    enqueueSnackbar('Final invoice has been sent', { variant: 'success' });
  };

  // Handler to update company folder
  const handleUpdateCompanyFolder = async (folderName: string) => {
    if (!currentCompany?.id) {
      const errorMessage = 'Company ID not available. Please ensure the company is properly linked.';
      enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 8000 });
      Logger.error('Company ID not available', {
        currentCompany,
        companyMeasurementDateByAttioId,
      });
      return;
    }

    try {
      await ApiService.apiCompaniesPartialUpdate(currentCompany.id, {
        file_storage_reference: folderName,
        is_deleted: false,
      } as Parameters<typeof ApiService.apiCompaniesPartialUpdate>[1]);

      // Invalidate company measurement date query to refetch updated data
      await queryClient.invalidateQueries({
        queryKey: ['company-measurement-date-by-attio-valuation-id', valuationId],
      });

      enqueueSnackbar('Company folder updated successfully', { variant: 'success' });
    } catch (companyFolderError) {
      Logger.error('Error updating company folder:', companyFolderError);
      enqueueSnackbar('Failed to update company folder. Please try again.', { variant: 'error' });
    }
  };

  // Handler to update valuation folder
  const handleUpdateValuationFolder = async (folderName: string) => {
    // Wait for company measurement date to load if still loading
    if (isLoadingCompanyMeasurementDate) {
      enqueueSnackbar('Loading measurement date information, please wait...', { variant: 'info' });
      return;
    }

    // Try to get measurement date ID from multiple sources
    let measurementDateId: number | undefined;

    if (companyMeasurementDateByAttioId) {
      measurementDateId = companyMeasurementDateByAttioId.id;
    }

    if (!measurementDateId) {
      const errorMessage = companyMeasurementDateError
        ? 'Unable to find measurement date for this valuation. Please ensure the valuation is linked to a measurement date.'
        : 'Measurement date ID not available. Please try refreshing the page.';
      enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 8000 });
      Logger.error('Measurement date ID not available', {
        companyMeasurementDateByAttioId,
        valuationId,
        companyMeasurementDateError,
      });
      return;
    }

    try {
      await ApiService.apiCompanyMeasurementDatesPartialUpdate(measurementDateId, {
        file_storage_reference: folderName,
      });

      // Invalidate company measurement date query to refetch updated data
      await queryClient.invalidateQueries({
        queryKey: ['company-measurement-date-by-attio-valuation-id', valuationId],
      });

      enqueueSnackbar('Valuation folder updated successfully', { variant: 'success' });
    } catch (valuationFolderError) {
      Logger.error('Error updating valuation folder:', valuationFolderError);
      enqueueSnackbar('Failed to update valuation folder. Please try again.', { variant: 'error' });
    }
  };

  // Handle model support creation
  const handleCreateModelSupport = async () => {
    if (!appCompanyId) {
      enqueueSnackbar('Unable to create model support: Company information not available', {
        variant: 'error',
      });
      return;
    }

    try {
      // Use currentCompanyMeasurementDateId from global state, or fall back to the one fetched by attio_valuation_id
      const measurementDateId = companyMeasurementDateByAttioId?.id;
      if (!measurementDateId) {
        if (companyMeasurementDateError) {
          enqueueSnackbar(
            `Cannot create model support for ${currentCompany?.name || 'this company'}. The measurement date for this valuation (${valuationId}) was not found. Please check if the valuation ID is correct.`,
            {
              variant: 'error',
              autoHideDuration: 10000,
            }
          );
        } else {
          enqueueSnackbar(
            `Cannot create model support for ${currentCompany?.name || 'this company'}. No measurement dates exist for this company. Please create a measurement date first in the company dashboard.`,
            {
              variant: 'warning',
              autoHideDuration: 8000,
            }
          );
        }
        return;
      }

      await createFulfillmentMutation.mutateAsync({
        company_measurement_date: measurementDateId,
        model_status: modelStatusMap[ModelStatusEnum.DRAFT], // Start with status 0 (draft)
        supporting_due_date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Tomorrow's date in YYYY-MM-DD format
        reviewers: [],
        preparers: [],
      });

      // Refetch fulfillment data to update the UI
      refetchFulfillments();
    } catch (fulfillmentError) {
      Logger.error('Failed to create model support:', fulfillmentError);
      // Error handling is already done in the mutation
    }
  };

  const handleModelTeamChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!existingFulfillment || !appCompanyId || !valuationId) {
      enqueueSnackbar('Unable to update model team: Company information not available', {
        variant: 'error',
      });
      return;
    }
    try {
      await ApiService.apiDashboardCompanyOperationsFulfillmentPartialUpdate(appCompanyId, valuationId, {
        id: existingFulfillment.id,
        is_slc_model: event.target.checked
      } as Fulfillment);
      refetchFulfillments();
    } catch (error_) {
      Logger.error('Error updating model team:', error_);
      enqueueSnackbar('Failed to update model team. Please try again.', { variant: 'error' });
    }
  };

  const combinedIsLoading = isLoading;

  const dealBox = (
    <Box>
      <ValuationDealInfo
        deal={valuationDetail?.deal}
        isLoading={combinedIsLoading}
        onDealStageChange={handleDealStageChange}
        onSendFinalInvoice={handleSendFinalInvoice}
        isFromModels={isFromModels}
      />
    </Box>
  );

  const valuedEntityBox = (
    <Box>
      <ValuedEntity
        valuedEntity={valuationDetail?.valuedEntity}
        isLoading={combinedIsLoading}
        companyFolder={currentCompany?.file_storage_reference || null}
        onUpdateCompanyFolder={handleUpdateCompanyFolder}
      />
    </Box>
  );

  const modelStatusBox = (
    <Box>
      <ModelStatus
        fulfillmentData={fulfillmentData}
        isLoading={combinedIsLoading}
        onCreateModelSupport={handleCreateModelSupport}
        isCreatingModelSupport={createFulfillmentMutation.isPending}
        companyId={appCompanyId}
        valuationId={valuationId}
        companyMeasurementDateId={companyMeasurementDateByAttioId?.id}
        isSlcModel={existingFulfillment?.is_slc_model || false}
        onSlcModelChange={handleModelTeamChange}
      />
    </Box>
  );

  const notesBox = (
    <Box>
      <NotesDisplay 
        isFromModels={isFromModels}
      />
    </Box>
  );

  const valuedInformationFullWidth = (
    <Box sx={{ gridColumn: '1 / -1' }}>
      <ValuedInformation
        isFromModels={isFromModels}
        valuedInformation={valuationDetail?.valuedInformation}
        isLoading={combinedIsLoading}
        statusOptions={statusOptions}
        currentStatusId={optimisticStatus?.statusId ?? valuationDetail?.statusId}
        onStatusChange={handleStatusChange}
        isStatusLoading={combinedIsLoading || isLoadingStatusOptions}
        companyFolder={currentCompany?.file_storage_reference || null}
        valuationFolder={companyMeasurementDateByAttioId?.file_storage_reference || null}
        onUpdateValuationFolder={handleUpdateValuationFolder}
        undoProps={{ isUndoPending, secondsLeft, onUndo: handleUndo }}
      />
    </Box>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        '&:focus': {
          outline: 'none',
        },
        '&:focus-visible': {
          outline: 'none',
        },
      }}>
      <StyledModalBox onClick={e => e.stopPropagation()}>
        <CloseButtonContainer>
          <IconButton onClick={onClose} size="small" aria-label="close" sx={{ padding: '4px' }}>
            <CloseIcon sx={{ fontSize: '18px' }} />
          </IconButton>
        </CloseButtonContainer>
        {valuationId ? (
          <BentoGrid>
            {isFromModels ? (
              <>
                {dealBox}
                {valuedEntityBox}
                {modelStatusBox}
                {notesBox}
                {valuedInformationFullWidth}
              </>
            ) : (
              <>
                {valuedInformationFullWidth}
                {dealBox}
                {valuedEntityBox}
                {modelStatusBox}
                {notesBox}
              </>
            )}
          </BentoGrid>
        ) : (
          <EmptyOperationsMessage />
        )}
      </StyledModalBox>
    </Modal>
  );
};

export default OperationsModal;

